import _ from 'lodash';

import buildGroupBy from 'hooks/useV5ChartData/buildGroupBy';
import { hasQueryOverrides } from 'components/V5Gadget/seriesDataKey';
import { isSecondaryDimensionAInEffect } from 'components/V5GadgetForm/isSecondaryDimensionAEligible';
import limitModeTypeCheckers from 'components/V5GadgetForm/limitModeTypeCheckers';

import { toQueryOverrides } from '../seriesVariants';
import { withoutUndefined } from './dateSettings';

// The classic editor writes settings Studio doesn't have at their off
// values, e.g. trendByHourInterval: false on every chart it saves
const LEGACY_FLAGS = [
  'trendByHourInterval',
  'excludePartialIntervals',
  'groupByDayOfWeek',
  'groupByMonth',
  'showDataLabels',
  'reverseAxes',
] as const;

const LEGACY_SERIES_FLAGS = [
  'plotOnSeparateAxis',
  'plotAverage',
  'plotTrend',
  'opposite',
] as const;

const DIMENSION_FLAGS = ['isGroupByDayOfWeek', 'isGroupByMonth'] as const;

const SORT_FLAGS = ['isComparison', 'isAbsolute'] as const;

// What the renderer draws without stacked options (useStackedBarData,
// useStackedAreaData)
const DEFAULT_STACKED_BAR_OPTIONS: StackedBarOptions = {
  direction: 'horizontal',
  asPercentage: false,
  altPivot: false,
};
const DEFAULT_STACKED_AREA_OPTIONS: StackedAreaOptions = { altPivot: false };

// The largest limit the matrix treats as a top N without a limit mode (see
// isTopN in getMetricMatrix)
const MAX_IMPLIED_TOP_N = 100;

// A matrix whose larger (or zero) limit has no limit mode isn't a top N,
// so it lists every term: getAllCurrentAndPreviousTermResponses fetches
// them ignoring the limit. With a second dimension it's fetched another
// way, so that's left as saved.
export const isUnlimitedMatrix = (def: V5ChartDefinition): boolean =>
  def.gadgetType === 'matrix' &&
  !def.dimensionB &&
  !def.groupByLimitMode &&
  def.groupByLimit !== undefined &&
  (def.groupByLimit === 0 || def.groupByLimit > MAX_IMPLIED_TOP_N);

// Off flags that mean something: a matrix metric's total is shown unless
// it's turned off
const MEANINGFUL_FALSE_KEYS = ['isTotalEnabled'];

// Leaves out the values a chart reads the same as absent (off flags, empty
// text, lists and objects, nulls), however deep, so two charts that draw the
// same compare equal: both when reading a gadget as a Studio block and when
// listing what converting one would drop
export const withoutInertValues = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(withoutInertValues);
  }
  if (_.isPlainObject(value)) {
    return _.omitBy(
      _.mapValues(value as Record<string, unknown>, withoutInertValues),
      (v, key) =>
        v === undefined ||
        v === null ||
        (v === false && !MEANINGFUL_FALSE_KEYS.includes(key)) ||
        v === '' ||
        (Array.isArray(v) && v.length === 0) ||
        (_.isPlainObject(v) && _.isEmpty(v)),
    );
  }
  return value;
};

const withoutFalse = <T extends object>({
  value,
  keys,
}: {
  value: T;
  keys: readonly string[];
}): T => _.omitBy(value, (v, key) => keys.includes(key) && v === false) as T;

// A custom range bucket's id only keys the classic editor's list
const comparableDimension = (
  dimension: Dimension | undefined,
): Dimension | undefined => {
  if (!dimension) {
    return undefined;
  }
  const { rangeInput } = dimension;
  const comparable = withoutFalse({ value: dimension, keys: DIMENSION_FLAGS });
  return rangeInput && rangeInput.mode === 'custom'
    ? {
        ...comparable,
        rangeInput: {
          ...rangeInput,
          items: rangeInput.items.map(({ id, ...item }) => ({
            ...item,
            id: '',
          })),
        },
      }
    : comparable;
};

const isTermDimension = (dimension: Dimension | undefined): boolean =>
  !!dimension &&
  (dimension.fieldType === 'text' || dimension.fieldType === 'boolean');

// Charts (not matrices) group by terms only for a text or boolean dimension
// (see buildGroupBy). Without one, e.g. over time, a limit changes nothing,
// though the classic editor offers one.
const hasNoTerms = (def: V5ChartDefinition): boolean =>
  def.gadgetType !== 'matrix' &&
  !isTermDimension(def.dimensionA) &&
  !isTermDimension(def.dimensionB);

// A matrix without rows or columns of terms (dates, months, ranges...) gets
// no groupBy (buildGroupBy), so its limit never reaches the query
const isTermlessMatrix = (def: V5ChartDefinition): boolean =>
  def.gadgetType === 'matrix' &&
  !isTermDimension(def.dimensionA) &&
  !isTermDimension(def.dimensionB);

// Whether a matrix shows every row its query can return, so the order the
// rows come back in changes nothing (the grid sorts them): without columns,
// it fetches every term but for a top N (isTopN in getMetricMatrix), and an
// exclude top N leaves its N out itself. Without terms there's no groupBy.
const fetchesEveryRow = (def: V5ChartDefinition): boolean => {
  if (isTermlessMatrix(def)) {
    return true;
  }
  // Its limit also caps the secondary row dimension's terms (buildSubGroupBy)
  if (
    def.gadgetType !== 'matrix' ||
    def.dimensionB ||
    isSecondaryDimensionAInEffect(def)
  ) {
    return false;
  }
  const mode = def.groupByLimitMode;
  return mode
    ? mode !== 'top n'
    : !def.groupByLimit || def.groupByLimit > MAX_IMPLIED_TOP_N;
};

// Partial intervals are trimmed only from rows by date (toBarSeries,
// stackedFormatters, useTimeSeriesData, removePartialIntervalResponses),
// which only a matrix or a chart over time has. A smart card, or a chart of
// terms, draws every row.
const canHavePartialIntervals = (def: V5ChartDefinition): boolean =>
  def.gadgetType === 'matrix' ||
  def.dimensionA?.fieldType === 'date' ||
  !!def.trendByCalendarInterval ||
  !!def.trendByFixedIntervalDays ||
  !!def.trendByFiscalInterval;

// Bar charts colour by term only for a second dimension's terms (a palette or
// series colours apply by metric without one, see isColouredByMetric)
const isCustomColoured = (def: V5ChartDefinition): boolean =>
  (!!def.colorPalette && def.colorPalette !== 'default') ||
  def.series.some((s) => !!s.color);

// A bar chart's second dimension on the plain date groups nothing: the query
// groups only by a term (buildGroupBy, buildSubGroupBy) or a range
// (buildGroupByInterval), and toBarSeries never reads it. It only changes how
// custom colours apply.
const isInertDateDimensionB = (def: V5ChartDefinition): boolean => {
  const { dimensionB } = def;
  return (
    ['bar', 'horizontalBar', 'combo'].includes(def.gadgetType) &&
    !!dimensionB &&
    dimensionB.field === 'date' &&
    !dimensionB.rangeInput &&
    !dimensionB.isGroupByDayOfWeek &&
    !dimensionB.isGroupByMonth &&
    !isCustomColoured(def)
  );
};

// Data labels are drawn only by these charts' renderers (barOptions,
// horizontalBarOptions, waterfallOptions, useTreeMapData)
const DATA_LABELLED_GADGET_TYPES: GadgetType[] = [
  'bar',
  'horizontalBar',
  'combo',
  'waterfall',
  'treeMap',
];

// How a chart's (not a matrix's) terms are sorted: the groupBy its query
// gets (buildGroupBy). Without a metric, the terms sort by themselves, and any
// direction but 'asc' is descending (applySortBy in graphql). Undefined when
// the chart has no terms to sort.
// A sort by a metric the query doesn't fetch (one not drawn, e.g. since
// removed) sorts nothing in graphql, so the terms come back in name order.
// Series asking their own question fetch it to sort by (toQueryChartDef).
export const getRenderedChartSort = (
  def: V5ChartDefinition,
): { field: string; metricId?: string; direction: SortBy } | undefined => {
  const groupBy = buildGroupBy(def, true);
  if (!groupBy) {
    return undefined;
  }
  if (
    groupBy.sortByMetric &&
    !def.series.some((s) => s.metricId === groupBy.sortByMetric) &&
    !def.series.some((s) => hasQueryOverrides(s.queryOverrides))
  ) {
    return { field: groupBy.field, direction: 'asc' };
  }
  const direction: SortBy = groupBy.sort === 'asc' ? 'asc' : 'desc';
  return withoutUndefined({
    field: groupBy.field,
    metricId: groupBy.sortByMetric,
    direction,
  });
};

// A chart's sort as Studio saves it (buildSortParts). The classic editor's
// older ways of saving the same sort (a direction alone, a series flagged to
// sort by, a sort with no direction, a stale direction over the grouping's)
// and ones the chart doesn't use (a comparison sort, or any sort without
// terms) are made the same as what the query gets.
const comparableChartSort = (
  def: V5ChartDefinition,
): Pick<V5ChartDefinition, 'groupBySortBy' | 'grouping' | 'series'> => {
  const rendered = getRenderedChartSort(def);
  const { defaultSort, ...otherGrouping } = def.grouping ?? {};
  const series = def.series.map(
    ({ sortByMetricId, sortByComparison, ...s }) => s,
  );
  if (!rendered) {
    return {
      groupBySortBy: undefined,
      grouping: _.isEmpty(otherGrouping)
        ? undefined
        : (otherGrouping as GroupingOptions),
      series,
    };
  }
  const { field, metricId, direction } = rendered;
  return {
    groupBySortBy: direction,
    grouping: {
      ...otherGrouping,
      defaultSort: withoutUndefined({
        field: metricId ?? field,
        metricId,
        direction,
      }),
    },
    series,
  };
};

// A matrix's columns, as its grid names them: its rows' field, and each
// metric's value and change columns
const getMatrixColumnFields = (def: V5ChartDefinition): string[] => [
  ...(def.dimensionA ? [def.dimensionA.field] : []),
  ...def.series.flatMap((s) => [
    s.metricId,
    `${s.metricId}-delta`,
    `${s.metricId}-percentDelta`,
  ]),
];

// Whether a matrix's default sort does nothing: with rows that aren't terms
// (e.g. dates), it only picks the column the grid starts sorted by, so one
// naming a column the matrix doesn't have (e.g. a metric since removed)
// leaves the rows in their own order. Rows of terms send it to the query,
// so it's never inert there.
export const isInertMatrixSort = (def: V5ChartDefinition): boolean => {
  const field = def.grouping?.defaultSort?.field;
  return (
    def.gadgetType === 'matrix' &&
    !!field &&
    !isTermDimension(def.dimensionA) &&
    !isTermDimension(def.dimensionB) &&
    !getMatrixColumnFields(def).includes(field)
  );
};

// A matrix's sort as its grid and query draw it, in the form Studio saves
// (buildTableDefinition): a default sort, with the rows' direction when it
// has rows of terms. The classic editor's older ways of saving one are read
// as such:
// - a series flagged to sort by (the first, as buildGroupBy and the grid
//   take it), in the rows' direction
// - the rows' direction alone, which sorts the rows' own column (dates,
//   months, days...)
// - an exclude top N's display sort: the rows left are all fetched
//   (getExcludeTopNTermResponses) and sorted by the grid
// - on rows of terms, a sort naming a column the matrix doesn't have: the
//   query doesn't fetch that metric, so graphql leaves the terms in name
//   order. On other rows that sort is inert (isInertMatrixSort).
// - on rows of terms with no second dimension, or dates across
//   (getTermedHistogramResponses), no sort at all: the query sends no
//   direction, which graphql's applySortBy takes as the terms descending,
//   and buildRowData and the grid leave them so
// A series' comparison sort (e.g. 'delta asc') sorts nothing: the query
// doesn't read it (buildGroupBy), and the grid gets it as its change
// column's sort (getMatrixDefaultSort), which ag-grid takes as none
export const withRenderedMatrixSort = (
  def: V5ChartDefinition,
): V5ChartDefinition => {
  if (def.gadgetType !== 'matrix') {
    return def;
  }
  const { defaultSort, ...otherGrouping } = def.grouping ?? {};
  const flagged = def.series.find((s) => s.sortByMetricId);
  const termField = isTermDimension(def.dimensionA)
    ? def.dimensionA?.field
    : undefined;
  const rowsField = def.dimensionA?.field;
  const sort = ((): SortConfig | undefined => {
    if (defaultSort) {
      const { field, metricId } = defaultSort;
      // A sort by a metric's change column whose metric id names none of the
      // series: the grid sorts by the column, and while every row is shown
      // the query's sort by that id changes nothing
      const columnMetric = def.series.find(
        (s) =>
          field === `${s.metricId}-delta` ||
          field === `${s.metricId}-percentDelta`,
      );
      if (
        columnMetric &&
        isChangeColumnSort(defaultSort) &&
        !def.series.some((s) => s.metricId === metricId) &&
        fetchesEveryRow(def)
      ) {
        return { ...defaultSort, metricId: columnMetric.metricId };
      }
      const isStale =
        !!field &&
        !getMatrixColumnFields(def).includes(field) &&
        (!metricId || !def.series.some((s) => s.metricId === metricId));
      return termField && isStale
        ? { field: termField, direction: 'asc' }
        : undefined;
    }
    if (
      limitModeTypeCheckers.isExcludeTopN(def.groupByLimitMode) &&
      def.groupByLimitMode.displaySort
    ) {
      return def.groupByLimitMode.displaySort;
    }
    if (flagged) {
      return {
        field: flagged.metricId,
        metricId: flagged.metricId,
        direction: def.groupBySortBy ?? 'desc',
      };
    }
    if (def.groupBySortBy && rowsField) {
      return { field: rowsField, direction: def.groupBySortBy };
    }
    return termField && (!def.dimensionB || def.dimensionB.fieldType === 'date')
      ? { field: termField, direction: 'desc' }
      : undefined;
  })();
  const series = def.series.map(
    ({ sortByMetricId, sortByComparison, ...s }) => s,
  );
  if (!sort) {
    return { ...def, series };
  }
  return {
    ...def,
    series,
    groupBySortBy: termField ? sort.direction : undefined,
    grouping: { ...otherGrouping, defaultSort: sort } as GroupingOptions,
  };
};

// Whether a matrix sort is by a metric's change column, as
// buildTableDefinition saves a comparison sort, rather than its value
export const isChangeColumnSort = (sort: SortConfig): boolean =>
  !!sort.field &&
  (sort.field.endsWith('-delta') || sort.field.endsWith('-percentDelta'));

const comparableMatrixGrouping = (
  def: V5ChartDefinition,
): GroupingOptions | undefined => {
  const { grouping } = def;
  if (!grouping || !grouping.defaultSort) {
    return grouping;
  }
  if (isInertMatrixSort(def)) {
    const { defaultSort, ...rest } = grouping;
    return _.isEmpty(rest) ? undefined : (rest as GroupingOptions);
  }
  // The classic editor flags a sort by a metric's value as a comparison
  // when the series it was flagged on had one (V5GadgetForm), but the grid
  // sorts the column it names. Whether it's absolute still sets how the
  // change columns sort (buildMatrixColDefs).
  const { isComparison, ...valueSort } = grouping.defaultSort;
  return {
    ...grouping,
    defaultSort: withoutFalse({
      value: isChangeColumnSort(grouping.defaultSort)
        ? grouping.defaultSort
        : valueSort,
      keys: SORT_FLAGS,
    }),
  };
};

// Charts whose renderer never draws a series' displayName, which the
// classic editor writes on every metric it picks: cards and smart cards
// show the value under the chart's name; pies, funnels and packed bubbles
// name their series after the metric; a tree map after the chart; stacks
// after their terms; sankey and heatmap tooltips show only their nodes or
// cells
const SERIES_NAMELESS_GADGET_TYPES: GadgetType[] = [
  'card',
  'smartCard',
  'treeMap',
  'stackedBar',
  'stackedArea',
  'pie',
  'donut',
  'funnel',
  'pyramid',
  'packedBubble',
  'sankey',
  'heatmap',
];

const isSeriesNameless = (def: V5ChartDefinition): boolean =>
  SERIES_NAMELESS_GADGET_TYPES.includes(def.gadgetType);

// Averages and trends are drawn only by toBarSeries, which only these
// charts' renderers use
const TRENDED_GADGET_TYPES: GadgetType[] = [
  'bar',
  'horizontalBar',
  'combo',
  'waterfall',
  'radar',
];

// The classic editor gives every series it adds smartCardComparison: 'delta',
// which only a smart card's first series shows (useSmartCardData). Older
// gadgets have it misspelled (smartCardComparision), which smart cards still
// read, and nothing else does. Combo series without a type (or a blank one)
// are drawn as the chart's columns (barOptions). A combo's first series is
// drawn on the first axis, and a later one gets an axis, with its range and
// side, only when it's plotted on its own (toBarSeries). A matrix metric's
// total is shown unless it's turned off.
const comparableSeries = (def: V5ChartDefinition): V5ChartDefinitionSeries[] =>
  def.series.map((s, index) => {
    const { smartCardComparision: misspelled, ...series } =
      s as V5ChartDefinitionSeries & {
        smartCardComparision?: V5ChartDefinitionSeries['smartCardComparison'];
      };
    const {
      smartCardComparison,
      displayName,
      plotAverage,
      plotTrend,
      ...rest
    } = withoutFalse({
      value: series,
      keys: LEGACY_SERIES_FLAGS,
    });
    const isTrended = TRENDED_GADGET_TYPES.includes(def.gadgetType);
    const isCombo = def.gadgetType === 'combo';
    const isAxisless = isCombo && index > 0 && !rest.plotOnSeparateAxis;
    return withoutUndefined({
      ...rest,
      plotOnSeparateAxis:
        isCombo && index === 0 ? undefined : rest.plotOnSeparateAxis,
      minY: isAxisless ? undefined : rest.minY,
      maxY: isAxisless ? undefined : rest.maxY,
      minRangePercent: isAxisless ? undefined : rest.minRangePercent,
      opposite: isAxisless ? undefined : rest.opposite,
      isTotalEnabled: rest.isTotalEnabled === false ? false : undefined,
      plotAverage: isTrended ? plotAverage : undefined,
      plotTrend: isTrended ? plotTrend : undefined,
      displayName: isSeriesNameless(def) ? undefined : displayName,
      smartCardComparison:
        def.gadgetType === 'smartCard' && index === 0
          ? smartCardComparison ?? misspelled
          : undefined,
      type: def.gadgetType === 'combo' ? s.type || 'column' : s.type,
      // Empty overrides ask nothing different
      queryOverrides: s.queryOverrides
        ? toQueryOverrides({ metricId: s.metricId, ...s.queryOverrides })
        : undefined,
    });
  });

// A chart definition with the classic editor's ways of saving what Studio
// saves differently made the same, so charts that render the same compare
// equal: its off flags, empty description and settings for other chart types
// (it writes them all) left out, a sort as Studio saves it (see
// comparableChartSort), limits that change nothing dropped and absent stacked options as the
// renderer's defaults
// Trend cards bucket their one metric's dates themselves
// (useV5RollingTrendCardDefinition, useV5FixedTrendCardDefinition): the
// grouping, intervals and limits the classic editor leaves on them are
// ignored
const TREND_CARD_FIELDS: (keyof V5ChartDefinition)[] = [
  'id',
  'name',
  'description',
  'gadgetType',
  'series',
  'colorPalette',
];

// An exclude top N without a direction leaves out the highest
// (getExcludeTopNTermResponses), as Studio saves it
const comparableLimitMode = (
  mode: V5ChartDefinition['groupByLimitMode'],
): V5ChartDefinition['groupByLimitMode'] =>
  mode &&
  typeof mode === 'object' &&
  mode.type === 'exclude top n' &&
  mode.excludeSort &&
  !mode.excludeSort.direction
    ? { ...mode, excludeSort: { ...mode.excludeSort, direction: 'desc' } }
    : mode;

// Without the series the classic editor can leave with no metric (e.g. when
// one is removed): a matrix draws no column for one (buildMatrixColDefs)
export const withoutMetriclessSeries = (
  def: V5ChartDefinition,
): V5ChartDefinition =>
  def.series.every((s) => !!s.metricId)
    ? def
    : { ...def, series: def.series.filter((s) => !!s.metricId) };

export const toComparable = (saved: V5ChartDefinition): V5ChartDefinition => {
  const def =
    saved.gadgetType === 'matrix' ? withoutMetriclessSeries(saved) : saved;
  if (
    def.gadgetType === 'rollingTrendCard' ||
    def.gadgetType === 'fixedTrendCard'
  ) {
    const trendCard = _.pick(def, TREND_CARD_FIELDS) as V5ChartDefinition;
    return withoutUndefined<V5ChartDefinition>({
      ...trendCard,
      description: def.description || undefined,
      series: comparableSeries(trendCard),
    });
  }
  const isMatrix = def.gadgetType === 'matrix';
  const matrix = isMatrix ? withRenderedMatrixSort(def) : def;
  const grouping = isMatrix ? comparableMatrixGrouping(matrix) : undefined;
  const sort = isMatrix
    ? {
        grouping,
        groupBySortBy:
          !!matrix.groupBySortBy &&
          matrix.groupBySortBy === grouping?.defaultSort?.direction
            ? undefined
            : matrix.groupBySortBy,
        series: comparableSeries(matrix),
      }
    : comparableChartSort({ ...def, series: comparableSeries(def) });
  const isLimitIgnored =
    hasNoTerms(def) || isUnlimitedMatrix(def) || isTermlessMatrix(def);
  // A matrix reads its limit mode only without columns (getMetricMatrix):
  // with them, or without terms, its top N and show all are the same. Its
  // exclude top N is left as saved.
  const isMatrixModeIgnored =
    isMatrix &&
    (!!def.dimensionB || isTermlessMatrix(def)) &&
    !limitModeTypeCheckers.isExcludeTopN(def.groupByLimitMode);
  const isDataLabelled = DATA_LABELLED_GADGET_TYPES.includes(def.gadgetType);
  // A chart's (not a matrix's) query reads only its limit (buildGroupBy), so
  // it's drawn the same with no limit mode, a top N or show all. Its exclude
  // top N is left as saved.
  const isChartModeIgnored =
    !isMatrix && !limitModeTypeCheckers.isExcludeTopN(def.groupByLimitMode);
  const isRedundantLimitMode =
    isChartModeIgnored ||
    isUnlimitedMatrix(def) ||
    isMatrixModeIgnored ||
    (def.groupByLimitMode === 'show all' && def.groupByLimit === undefined) ||
    // A top 0 lists nothing, where a 0 without the mode lists every term
    (def.groupByLimitMode === 'top n' &&
      !!def.groupByLimit &&
      def.groupByLimit <= MAX_IMPLIED_TOP_N);
  // isSizedToFitOverridden was replaced by gridSizing (FWA-9994); only
  // legacy matrices read it
  const { groupByExclude, isSizedToFitOverridden, ...rest } = withoutFalse({
    value: def as V5ChartDefinition & { isSizedToFitOverridden?: boolean },
    keys: LEGACY_FLAGS,
  });
  return withoutUndefined<V5ChartDefinition>({
    ...rest,
    description: def.description || undefined,
    ...sort,
    dimensionA: comparableDimension(def.dimensionA),
    dimensionB: isInertDateDimensionB(def)
      ? undefined
      : comparableDimension(def.dimensionB),
    // Only while it splits the rows: left over, it changes nothing
    secondaryDimensionA: isSecondaryDimensionAInEffect(def)
      ? comparableDimension(def.secondaryDimensionA)
      : undefined,
    isSecondaryRowsStacked:
      (isSecondaryDimensionAInEffect(def) && def.isSecondaryRowsStacked) ||
      undefined,
    excludePartialIntervals: canHavePartialIntervals(def)
      ? rest.excludePartialIntervals
      : undefined,
    groupByLimit: isLimitIgnored ? undefined : def.groupByLimit,
    groupByLimitMode: isRedundantLimitMode
      ? undefined
      : comparableLimitMode(def.groupByLimitMode),
    // Only read as !== 'fitContent' and ?? 'last'
    gridSizing: def.gridSizing === 'fitGadget' ? undefined : def.gridSizing,
    totalsColumnPosition:
      def.totalsColumnPosition === 'last'
        ? undefined
        : def.totalsColumnPosition,
    // Drawn only by the charts that have them. Kept when data labels are
    // turned off, but only used while they're on.
    showDataLabels: isDataLabelled ? rest.showDataLabels : undefined,
    labelStep: isDataLabelled && def.showDataLabels ? def.labelStep : undefined,
    // Only a pie is drawn solid: a donut keeps its hole
    isSolid: (def.gadgetType === 'pie' && def.isSolid) || undefined,
    // Left over from the type the classic editor first had
    stackedBarOptions:
      def.gadgetType === 'stackedBar'
        ? def.stackedBarOptions ?? DEFAULT_STACKED_BAR_OPTIONS
        : undefined,
    stackedAreaOptions:
      def.gadgetType === 'stackedArea'
        ? def.stackedAreaOptions ?? DEFAULT_STACKED_AREA_OPTIONS
        : undefined,
  });
};

// A chart Studio rebuilt, keeping the off flags the stored one had. Studio
// doesn't set them, and not all are the same as absent (e.g. groupByDayOfWeek:
// false changes how getMetrics fetches totals), so the gadget keeps fetching
// as it did. A chart that doesn't show its series' names keeps them too,
// for other readers (e.g. Insights). Studio's own settings win.
export const withLegacyDefaults = ({
  built,
  stored,
}: {
  built: V5ChartDefinition;
  stored: V5ChartDefinition;
}): V5ChartDefinition => {
  const storedFlags = _.pickBy(
    _.pick(stored, LEGACY_FLAGS),
    (v) => v === false,
  );
  return {
    ...storedFlags,
    ...withoutUndefined(built),
    series: built.series.map((s) => {
      // The same metric can be drawn more than once (see seriesVariants)
      const previous =
        stored.series.find((p) => p.id === s.id) ??
        stored.series.find(
          (p) =>
            p.metricId === s.metricId && !hasQueryOverrides(p.queryOverrides),
        );
      return previous
        ? {
            ..._.pickBy(
              _.pick(previous, LEGACY_SERIES_FLAGS),
              (v) => v === false,
            ),
            ...(isSeriesNameless(built) && previous.displayName
              ? { displayName: previous.displayName }
              : {}),
            ...s,
          }
        : s;
    }),
  };
};
